// Colour tokens (design-tokens.md). Values come from CSS variables generated by
// @datascratch/design-tokens; the fallbacks below are the contract's light-theme values so
// the editor renders sensibly before the design-system package lands.

export const TOKEN_FALLBACKS: Record<string, string> = {
  "bg.canvas": "#FAF8F4",
  "bg.surface": "#FFFFFF",
  "bg.elevated": "#FFFFFFCC",
  "text.primary": "#1D1C1A",
  "text.secondary": "#6B6862",
  "text.tertiary": "#9C978E",
  "line.separator": "#E6E1D8",
  accent: "#2E5BDB",
  danger: "#D2453B",
  success: "#3E8E5A",
  warning: "#C98A1B",
  "ink.black": "#1D1C1A",
  "ink.blue": "#2E5BDB",
  "ink.red": "#D2453B",
  "ink.green": "#3E8E5A",
  "ink.purple": "#7A4FD6",
  "ink.brown": "#8A5A32",
  "highlight.yellow": "#FFE36E80",
  "highlight.green": "#9BE3A880",
  "highlight.pink": "#FFB3C780",
  "highlight.blue": "#A9CCFF80",
  "tint.red.fg": "#C2412F",
  "tint.orange.fg": "#C46A1C",
  "tint.yellow.fg": "#A87F12",
  "tint.green.fg": "#3E8E5A",
  "tint.teal.fg": "#2B8A86",
  "tint.blue.fg": "#2E5BDB",
  "tint.indigo.fg": "#4B4FC4",
  "tint.purple.fg": "#7A4FD6",
  "tint.pink.fg": "#C2477F",
  "tint.gray.fg": "#6B6862",
  "tint.red.bg": "#FBE7E3",
  "tint.orange.bg": "#FCEEDF",
  "tint.yellow.bg": "#FBF3D6",
  "tint.green.bg": "#E5F2E8",
  "tint.teal.bg": "#E1F2F1",
  "tint.blue.bg": "#E5ECFB",
  "tint.indigo.bg": "#E8E8F9",
  "tint.purple.bg": "#EFE8FA",
  "tint.pink.bg": "#FAE6EF",
  "tint.gray.bg": "#EFECE6",
};

export const INK_TOKENS = ["ink.black", "ink.blue", "ink.red", "ink.green", "ink.purple", "ink.brown"] as const;
export const HIGHLIGHT_TOKENS = ["highlight.yellow", "highlight.green", "highlight.pink", "highlight.blue"] as const;
export const TINTS = ["red", "orange", "yellow", "green", "teal", "blue", "indigo", "purple", "pink", "gray"] as const;

/** `ink.blue` → `--ds-ink-blue`. */
export function tokenVarName(token: string): string {
  return `--ds-${token.replace(/\./g, "-")}`;
}

const HEX_COLOR = /^#[0-9a-f]{3,8}$/i;
/** A token name: dotted lower-case words (`ink.blue`, `tint.red.fg`) — nothing CSS could read as syntax. */
const TOKEN_NAME = /^[a-z][a-z0-9]*(\.[a-z0-9]+){0,3}$/;

/**
 * The colour if it is a literal `#rgb`…`#rrggbbaa` or a token name, else null. Colours come from
 * documents and presence written by other clients: anything else could inject CSS into a style.
 */
export function safeColor(value: unknown): string | null {
  if (typeof value !== "string") return null;
  return HEX_COLOR.test(value) || TOKEN_NAME.test(value) ? value : null;
}

/** CSS value for a colour token or a literal `#RRGGBB`; invalid values render as `inherit`. */
export function colorTokenVar(value: string | null | undefined): string {
  const token = safeColor(value);
  if (!token) return "inherit";
  if (token.startsWith("#")) return token;
  const fallback = TOKEN_FALLBACKS[token] ?? "currentColor";
  return `var(${tokenVarName(token)}, ${fallback})`;
}

/** Concrete colour for canvas drawing; reads the live CSS variable (theme-aware). */
export function resolveColor(value: string, el: Element | null): string {
  const token = safeColor(value) ?? "ink.black";
  if (token.startsWith("#")) return token;
  if (el) {
    const v = getComputedStyle(el).getPropertyValue(tokenVarName(token)).trim();
    if (v) return v;
  }
  return TOKEN_FALLBACKS[token] ?? "#1D1C1A";
}
